import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Typography,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  MenuItem,
  Badge,
  Box,
  Tooltip,
} from "@mui/material";
import { Plus, Upload, Square, FileText, X, BarChart2, Eye, ArrowUp, ArrowDown, GripVertical, Trash2 } from 'lucide-react';
import { useNavigate } from "react-router-dom"
import api from '../utils/api';

// Fonction utilitaire pour formater la date
const formatDate = (dateString) => {
  if (!dateString || dateString === "Jamais") return dateString;
  const date = new Date(dateString);
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).replace(',', '');
};

// Liste des marketplaces disponibles
const MARKETPLACES = [
  { id: "DE", name: "Allemagne" },
  { id: "IT", name: "Italie" },
  { id: "FR", name: "France" },
  { id: "ES", name: "Espagne" }
];

function FournisseursFilesPage() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [runningFiles, setRunningFiles] = useState({});
  const [progress, setProgress] = useState({});
  const [openLogs, setOpenLogs] = useState(false);
  const [selectedLogs, setSelectedLogs] = useState("");
  const [selectedFile, setSelectedFile] = useState("");
  const [analysedFiles, setAnalysedFiles] = useState([]);
  const [openFileUpload, setOpenFileUpload] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);
  const navigate = useNavigate();
  
  // États pour la pop-up de sélection des marketplaces
  const [openMarketplaceDialog, setOpenMarketplaceDialog] = useState(false);
  const [selectedMarketplaces, setSelectedMarketplaces] = useState([]);
  
  // États pour le tri, le survol et la sélection des lignes
  const [sorting, setSorting] = useState([{ id: "lastCompleted", desc: true }]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [draggedColumnId, setDraggedColumnId] = useState(null);
  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem('fournisseursFilesColumnOrder');
    return savedOrder ? JSON.parse(savedOrder) : [];
  });
  const [columnSizing, setColumnSizing] = useState(() => {
    const savedSizing = localStorage.getItem('fournisseursFilesColumnSizing');
    if (savedSizing) {
      return JSON.parse(savedSizing);
    }
    // Valeurs par défaut
    return {
      fileName: 250,
      progress: 200,
      lineCount: 100,
      lastCompleted: 150,
      status: 120,
      actions: 200,
    };
  });

  useEffect(() => {
    fetchFiles();
    fetchProgress();
    fetchAnalysedFiles();
    const interval = setInterval(fetchProgress, 2000);
    return () => clearInterval(interval);
  }, []);

  // Sauvegarder la configuration des colonnes
  useEffect(() => {
    if (columnOrder.length > 0) {
      localStorage.setItem('fournisseursFilesColumnOrder', JSON.stringify(columnOrder));
    }
  }, [columnOrder]);

  useEffect(() => {
    if (Object.keys(columnSizing).length > 0) {
      localStorage.setItem('fournisseursFilesColumnSizing', JSON.stringify(columnSizing));
    }
  }, [columnSizing]);

  const fetchFiles = () => {
    setLoading(true);
    api.get("/api/fournisseurs-files")
      .then((res) => {
        if (res.data.success) {
          setFiles(res.data.data);
        } else {
          console.error('Erreur lors de la récupération des fichiers:', res.data.message);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  const fetchProgress = () => {
    api.get("/api/fournisseurs-files/progress")
      .then((res) => {
        if (res.data.success) {
          setProgress(res.data.data);
          const running = {};
          Object.keys(res.data.data).forEach((fileName) => {
            if (res.data.data[fileName].status === "En cours..." || 
                res.data.data[fileName].status === "running") {
              running[fileName] = true;
            }
          });
          setRunningFiles(running);
        } else {
          console.error('Erreur lors de la récupération de la progression:', res.data.message);
        }
      })
      .catch((err) => console.error(err));
  };

  const fetchAnalysedFiles = async () => {
    try {
      const filesRes = await api.get("/api/analysed-files");
      if (filesRes.data.success) {
        setAnalysedFiles(filesRes.data.data || []);
      } else {
        console.error('Erreur lors de la récupération des fichiers analysés:', filesRes.data.message);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des fichiers analysés:', error);
    }
  };

  const handleOpenLogs = (fileName) => {
    setSelectedFile(fileName);
    setOpenLogs(true);
    fetchLog(fileName);
  };

  const handleCloseLogs = () => {
    setOpenLogs(false);
    setSelectedLogs("");
  };

  const handleOpenFileUpload = () => {
    setOpenFileUpload(true);
  };

  const handleCloseFileUpload = () => {
    setOpenFileUpload(false);
    setFileToUpload(null);
  };

  const handleFileChange = (event) => {
    setFileToUpload(event.target.files[0]);
  };

  const handleUploadFile = () => {
    if (!fileToUpload) return;

    const formData = new FormData();
    formData.append('file', fileToUpload);

    api.post("/api/fournisseurs-files/upload", formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    .then(() => {
      handleCloseFileUpload();
      fetchFiles();
      fetchProgress();
    })
    .catch((error) => {
      console.error('Erreur lors de l\'upload du fichier:', error);
      alert('Erreur lors de l\'upload du fichier');
    });
  };

  const handleOpenMarketplaceDialog = (file) => {
    setSelectedFile(file);
    setOpenMarketplaceDialog(true);
  };

  const handleCloseMarketplaceDialog = () => {
    setOpenMarketplaceDialog(false);
    setSelectedMarketplaces([]);
  };

  const handleMarketplaceChange = (event) => {
    const value = event.target.value;
    setSelectedMarketplaces(typeof value === 'string' ? value.split(',') : value);
  };

  const handleAnalyze = () => {
    // Fermer la modal immédiatement
    handleCloseMarketplaceDialog();
    
    // Lancer l'analyse en arrière-plan
    api.post("/api/analyze/file", {
        fileName: selectedFile,
        marketplaceFilter: selectedMarketplaces.length > 0 ? selectedMarketplaces : undefined,
        sourceType: 'supplier',  // S'assurer que c'est toujours 'supplier' pour les fichiers fournisseurs
        scriptName: selectedFile  // Ajouter le nom du fichier comme script_name
    })
    .then(() => {
        fetchProgress();
    })
    .catch((error) => {
        console.error('Erreur lors de l\'analyse:', error);
    });
  };

  const handleStopAnalysis = (fileName) => {
    api.post("/api/fournisseurs-files/stop", {
        fileName: fileName,
      })
      .then(fetchProgress)
      .catch((err) => {
        console.error(err);
        alert("Erreur lors de l'arrêt de l'analyse");
      });
  };

  const handleViewFile = (fileName) => {
    // Ajouter l'extension .json directement
    const jsonFileName = `${fileName}.json`;
    navigate('/analyse', { state: { sourceFile: jsonFileName } });
  };

  const handleDeleteFile = async (fileName) => {
    try {
      // Trouver le fichier dans la liste des fichiers pour obtenir son ID
      const fileToDelete = files.find(f => f.fileName === fileName);
      if (!fileToDelete) {
        console.error("Fichier non trouvé");
        return;
      }

      await api.delete(`/api/fournisseurs-files/${fileToDelete.id}`);
      // Rafraîchir la liste des fichiers
      fetchFiles();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
    }
  };

  const isFileNew = (fileName) => {
    const jsonFileName = fileName + '.json';
    const file = analysedFiles.find(f => f.name === jsonFileName);
    return file && file.isNew;
  };

  const fileExists = (fileName) => {
    // Vérifier si un fichier analysé existe pour ce fichier
    const jsonFileName = fileName + '.json';
    return analysedFiles.some(f => f.name === jsonFileName);
  };

  // Fonction pour trier les fichiers
  const getSortedFiles = () => {
    if (!files.length) return [];
    
    return [...files].sort((a, b) => {
      const aProgress = progress[a.fileName] || {};
      const bProgress = progress[b.fileName] || {};
      
      if (sorting[0].id === "fileName") {
        return sorting[0].desc 
          ? b.fileName.localeCompare(a.fileName) 
          : a.fileName.localeCompare(b.fileName);
      }
      
      if (sorting[0].id === "lineCount") {
        return sorting[0].desc 
          ? (b.lineCount || 0) - (a.lineCount || 0) 
          : (a.lineCount || 0) - (b.lineCount || 0);
      }
      
      if (sorting[0].id === "status") {
        const aStatus = aProgress.status || a.status || "";
        const bStatus = bProgress.status || b.status || "";
        return sorting[0].desc 
          ? bStatus.localeCompare(aStatus) 
          : aStatus.localeCompare(bStatus);
      }
      
      // Par défaut, tri par date de dernière complétion
      const aDate = aProgress.lastCompleted ? new Date(aProgress.lastCompleted) : new Date(0);
      const bDate = bProgress.lastCompleted ? new Date(bProgress.lastCompleted) : new Date(0);
      
      return sorting[0].desc ? bDate - aDate : aDate - bDate;
    });
  };

  // Fonction pour gérer le changement de tri
  const handleSortChange = (columnId) => {
    setSorting(prev => {
      const currentSort = prev.find(sort => sort.id === columnId);
      if (currentSort) {
        // Si la colonne est déjà triée, inverser la direction
        return prev.map(sort => 
          sort.id === columnId 
            ? { ...sort, desc: !sort.desc } 
            : sort
        );
      } else {
        // Sinon, ajouter un nouveau tri
        return [{ id: columnId, desc: true }];
      }
    });
  };

  // Fonction pour gérer le survol des lignes
  const handleRowHover = (fileName) => {
    setHoveredRowId(fileName);
  };

  // Fonction pour gérer la sélection des lignes
  const handleRowClick = (fileName) => {
    setSelectedRowId(selectedRowId === fileName ? null : fileName);
  };

  const [logLoading, setLogLoading] = useState(false);
  const [logContent, setLogContent] = useState("");

  const fetchLog = async (fileName) => {
    setLogLoading(true);
    try {
      const response = await api.get(`/api/analyze/logs/${fileName}`);
      setLogContent(response.data.log || "Aucun log disponible");
    } catch (error) {
      console.error("Erreur lors du chargement du log:", error);
      setLogContent("Erreur lors du chargement du log");
    } finally {
      setLogLoading(false);
    }
  };

  return (
    <div style={{ padding: "20px", maxWidth: "900px", margin: "0 auto" }}>
      <Typography variant="h4" gutterBottom>
        Analyse des Fichiers Fournisseurs 🔍
      </Typography>

      <Button
        variant="contained"
        color="primary"
        startIcon={<Plus size={20} />}
        onClick={handleOpenFileUpload}
        style={{ marginBottom: "20px" }}
      >
        Ajouter un fichier
      </Button>

      {loading && (
        <CircularProgress style={{ display: "block", margin: "20px auto" }} />
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                style={{
                  width: columnSizing.fileName,
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={() => handleSortChange("fileName")}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <GripVertical 
                    sx={{ 
                      mr: 0.5, 
                      opacity: 0.2,
                      fontSize: '1rem',
                      '&:hover': { opacity: 0.5 }
                    }}
                  />
                  <strong>Nom du Fichier</strong>
                  {sorting.find(s => s.id === "fileName") && (
                    sorting.find(s => s.id === "fileName").desc ? 
                      <ArrowDown size={16} style={{ marginLeft: 4 }} /> : 
                      <ArrowUp size={16} style={{ marginLeft: 4 }} />
                  )}
                </Box>
              </TableCell>
              <TableCell
                style={{
                  width: columnSizing.lineCount,
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={() => handleSortChange("lineCount")}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <GripVertical 
                    style={{ 
                      marginRight: 4, 
                      opacity: 0.2,
                      fontSize: '1rem',
                      '&:hover': { opacity: 0.5 }
                    }}
                  />
                  <strong>Lignes</strong>
                  {sorting.find(s => s.id === "lineCount") && (
                    sorting.find(s => s.id === "lineCount").desc ? 
                      <ArrowDown size={16} style={{ marginLeft: 4 }} /> : 
                      <ArrowUp size={16} style={{ marginLeft: 4 }} />
                  )}
                </Box>
              </TableCell>
              <TableCell
                style={{
                  width: columnSizing.progress,
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={() => handleSortChange("progress")}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <GripVertical 
                    style={{ 
                      marginRight: 4, 
                      opacity: 0.2,
                      fontSize: '1rem',
                      '&:hover': { opacity: 0.5 }
                    }}
                  />
                  <strong>Progression</strong>
                  {sorting.find(s => s.id === "progress") && (
                    sorting.find(s => s.id === "progress").desc ? 
                      <ArrowDown size={16} style={{ marginLeft: 4 }} /> : 
                      <ArrowUp size={16} style={{ marginLeft: 4 }} />
                  )}
                </Box>
              </TableCell>
              <TableCell
                style={{
                  width: columnSizing.status,
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={() => handleSortChange("status")}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <GripVertical 
                    style={{ 
                      marginRight: 4, 
                      opacity: 0.2,
                      fontSize: '1rem',
                      '&:hover': { opacity: 0.5 }
                    }}
                  />
                  <strong>Statut</strong>
                  {sorting.find(s => s.id === "status") && (
                    sorting.find(s => s.id === "status").desc ? 
                      <ArrowDown size={16} style={{ marginLeft: 4 }} /> : 
                      <ArrowUp size={16} style={{ marginLeft: 4 }} />
                  )}
                </Box>
              </TableCell>
              <TableCell
                style={{
                  width: columnSizing.lastCompleted,
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={() => handleSortChange("lastCompleted")}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <GripVertical 
                    style={{ 
                      marginRight: 4, 
                      opacity: 0.2,
                      fontSize: '1rem',
                      '&:hover': { opacity: 0.5 }
                    }}
                  />
                  <strong>Dernier Scan</strong>
                  {sorting.find(s => s.id === "lastCompleted") && (
                    sorting.find(s => s.id === "lastCompleted").desc ? 
                      <ArrowDown size={16} style={{ marginLeft: 4 }} /> : 
                      <ArrowUp size={16} style={{ marginLeft: 4 }} />
                  )}
                </Box>
              </TableCell>
              <TableCell
                style={{
                  width: columnSizing.actions,
                  userSelect: "none",
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <GripVertical 
                    style={{ 
                      marginRight: 4, 
                      opacity: 0.2,
                      fontSize: '1rem',
                      '&:hover': { opacity: 0.5 }
                    }}
                  />
                  <strong>Actions</strong>
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getSortedFiles().map((file) => {
              const fileProgress = progress[file.fileName] || {};
              const percentage = fileProgress.progress
                ? (parseInt(fileProgress.progress.split("/")[0]) /
                    parseInt(fileProgress.progress.split("/")[1])) *
                  100
                : 0;
              
              const status = fileProgress.status || file.status || "-";
              const progressText = fileProgress.progress || "-";
              const isProcessRunning = fileProgress.status === "running" || fileProgress.status === "En cours...";
              const hasNewFile = isFileNew(file.fileName);

              return (
                <TableRow 
                  key={file.fileName}
                  onClick={(e) => {
                    // Ne pas sélectionner la ligne si on clique sur un élément interactif
                    if (e.target.closest('button, a, [role="button"], .MuiIconButton-root')) {
                      return;
                    }
                    handleRowClick(file.fileName);
                  }}
                  onMouseEnter={() => handleRowHover(file.fileName)}
                  onMouseLeave={() => setHoveredRowId(null)}
                  style={{
                    backgroundColor: file.fileName === selectedRowId 
                      ? 'rgba(33, 150, 243, 0.15)' 
                      : file.fileName === hoveredRowId 
                        ? 'rgba(0, 0, 0, 0.04)' 
                        : 'transparent',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  <TableCell>{file.fileName}</TableCell>
                  <TableCell>{file.lineCount || "-"}</TableCell>
                  <TableCell>
                    {runningFiles[file.fileName] ? (
                      <>
                        <Typography variant="body2">
                          {progressText} 
                          ({isProcessRunning ? "Analyse en cours..." : "-"})
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          style={{
                            height: "10px",
                            marginTop: "5px",
                            borderRadius: "5px",
                          }}
                        />
                      </>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    {status}
                  </TableCell>
                  <TableCell>
                    {formatDate(fileProgress.lastCompleted || file.lastCompleted)}
                  </TableCell>
                  <TableCell>
                    <div style={{ display: "flex", gap: "8px" }}>
                      {!runningFiles[file.fileName] ? (
                        <>
                          <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<BarChart2 size={20} />}
                            onClick={() => handleOpenMarketplaceDialog(file.fileName)}
                            disabled={status === "pending" || !file.fileName}
                            title={status === "pending" ? "Fichier en attente d'upload" : "Analyser le fichier"}
                          >
                            Analyser
                          </Button>
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenLogs(file.fileName)}
                          >
                            <FileText size={20} />
                          </IconButton>
                          {fileExists(file.fileName) && (
                            <Badge color="error" variant="dot" invisible={!hasNewFile}>
                              <IconButton
                                color="primary"
                                onClick={() => handleViewFile(file.fileName)}
                                title="Voir le fichier analysé"
                              >
                                <Eye size={20} />
                              </IconButton>
                            </Badge>
                          )}
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteFile(file.fileName)}
                            title="Supprimer le fichier"
                          >
                            <Trash2 size={20} />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="contained"
                            color="error"
                            startIcon={<Square size={20} />}
                            onClick={() => handleStopAnalysis(file.fileName)}
                          >
                            Arrêter
                          </Button>
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenLogs(file.fileName)}
                          >
                            <FileText size={20} />
                          </IconButton>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialogue d'upload de fichier */}
      <Dialog open={openFileUpload} onClose={handleCloseFileUpload}>
        <DialogTitle>
          Uploader un fichier fournisseur
          <IconButton
            aria-label="close"
            onClick={handleCloseFileUpload}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileChange}
            style={{ margin: "20px 0" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFileUpload}>Annuler</Button>
          <Button 
            onClick={handleUploadFile} 
            variant="contained" 
            color="primary"
            startIcon={<Upload size={20} />}
            disabled={!fileToUpload}
          >
            Uploader
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue de logs */}
      <Dialog open={openLogs} onClose={handleCloseLogs} maxWidth="md" fullWidth>
        <DialogTitle>
          Logs de {selectedFile}
          <IconButton
            aria-label="close"
            onClick={handleCloseLogs}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {logLoading ? (
            <CircularProgress />
          ) : (
            <pre style={{ whiteSpace: "pre-wrap" }}>{logContent}</pre>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialogue de sélection des marketplaces */}
      <Dialog open={openMarketplaceDialog} onClose={handleCloseMarketplaceDialog}>
        <DialogTitle>
          Sélectionner les marketplaces
          <IconButton
            aria-label="close"
            onClick={handleCloseMarketplaceDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <FormControl sx={{ m: 1, width: 300 }}>
            <InputLabel>Marketplaces</InputLabel>
            <Select
              multiple
              value={selectedMarketplaces}
              onChange={handleMarketplaceChange}
              input={<OutlinedInput label="Marketplaces" />}
              renderValue={(selected) => selected.join(', ')}
            >
              <MenuItem value="FR">France</MenuItem>
              <MenuItem value="DE">Allemagne</MenuItem>
              <MenuItem value="IT">Italie</MenuItem>
              <MenuItem value="ES">Espagne</MenuItem>
              <MenuItem value="UK">Royaume-Uni</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMarketplaceDialog}>Annuler</Button>
          <Button onClick={handleAnalyze} variant="contained" color="primary">
            Analyser
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default FournisseursFilesPage; 